import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { usePeopleFlowDevicesByClinicId } from '@/hooks/usePeopleFlowDevice'
import { peopleFlowDeviceService } from '@/services/people_flow_device_service'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import { usePeopleFlowDeviceByBroadcastDevice } from '@/hooks/usePeopleFlowDeviceByBroadcastDevice'

type PeopleFlowDeviceSettingProps = {
  clinicId: number
  broadcastDeviceId?: number
  onSuccess?: () => void
  isLoading?: boolean
}

export function PeopleFlowDeviceSetting({
  clinicId,
  broadcastDeviceId,
  onSuccess,
  isLoading: parentLoading,
}: PeopleFlowDeviceSettingProps) {
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | undefined>(undefined)
  const [isSaving, setIsSaving] = useState(false)

  const { devices, isLoading: isLoadingDevices } = usePeopleFlowDevicesByClinicId(clinicId)
  const { device: currentPeopleFlowDevice } =
    usePeopleFlowDeviceByBroadcastDevice(broadcastDeviceId)
  const { token } = useAuth()

  // 當 currentPeopleFlowDeviceId 變更時，更新選擇的裝置
  useEffect(() => {
    if (!currentPeopleFlowDevice) return
    setSelectedDeviceId(currentPeopleFlowDevice?.people_flow_device_id)
  }, [currentPeopleFlowDevice])

  const handleSave = async () => {
    if (!broadcastDeviceId || !selectedDeviceId) {
      toast.error('請選擇一個人流監控裝置')
      return
    }

    setIsSaving(true)
    try {
      const response = await peopleFlowDeviceService.setPeopleFlowDeviceForBroadcastDevice(
        broadcastDeviceId,
        selectedDeviceId,
        token ?? '',
      )

      if (response?.success) {
        toast.success('儲存成功')
        if (onSuccess) onSuccess()
      } else {
        toast.error(response?.error?.message || '發生未知錯誤')
      }
    } catch {
      toast.error('發生未知錯誤')
    } finally {
      setIsSaving(false)
    }
  }

  const isLoading = parentLoading || isLoadingDevices

  return (
    <Card>
      <CardHeader>
        <CardTitle>人流裝置設定</CardTitle>
        <CardDescription>
          選擇要綁定到此廣播裝置的人流監控裝置，一個廣播裝置只能綁定一個人流裝置
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : devices && devices.length > 0 ? (
          <>
            <RadioGroup
              value={selectedDeviceId?.toString()}
              onValueChange={(value) => setSelectedDeviceId(parseInt(value, 10))}
              className="space-y-4"
            >
              {devices.map((device) => (
                <div
                  key={device.people_flow_device_id}
                  className="flex items-center space-x-2 rounded-md border p-4"
                >
                  <RadioGroupItem
                    value={device.people_flow_device_id.toString()}
                    id={`device-${device.people_flow_device_id}`}
                  />
                  <Label
                    htmlFor={`device-${device.people_flow_device_id}`}
                    className="flex-1 cursor-pointer"
                  >
                    <div className="font-medium">{device.name}</div>
                    <div className="text-sm text-muted-foreground">IP: {device.ip_address}</div>
                  </Label>
                </div>
              ))}
            </RadioGroup>

            <div className="mt-6 flex justify-end">
              <Button onClick={handleSave} disabled={isSaving || !selectedDeviceId}>
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                儲存設定
              </Button>
            </div>
          </>
        ) : (
          <div className="py-8 text-center text-muted-foreground">
            <p>此診所尚未設定任何人流監控裝置</p>
            <p className="mt-2 text-sm">請先在人流監控裝置管理中新增裝置</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
